/**
 * MuscleBalance — horizontal bar chart showing push/pull/legs/core
 * set distribution over the last 30 days.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { MuscleGroup } from '@/types';

const PUSH_MUSCLES  = new Set<MuscleGroup>(['chest','front_delt','lateral_delt','triceps']);
const PULL_MUSCLES  = new Set<MuscleGroup>(['lats','traps','rhomboids','rear_delt','biceps','forearms']);
const LEGS_MUSCLES  = new Set<MuscleGroup>(['quads','hamstrings','glutes','calves','hip_flexors']);
const CORE_MUSCLES  = new Set<MuscleGroup>(['core','lower_back']);

function Bar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-ink-muted font-medium">{label}</span>
        <span className="text-ink-faint font-mono">{count} sets · {pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 bg-surface-300 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function MuscleBalance() {
  const data = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 30 * 86_400_000).toISOString();
    const sets = await db.loggedSets
      .where('loggedAt').aboveOrEqual(cutoff)
      .filter((s) => !s.isWarmup && s.rpe >= 7)
      .toArray();

    const exerciseIds = [...new Set(sets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exerciseIds);
    const exMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

    let push = 0, pull = 0, legs = 0, core = 0;
    for (const s of sets) {
      const ex = exMap.get(s.exerciseId);
      if (!ex) continue;
      const primary = ex.muscles[0]?.muscle;
      if (!primary) continue;
      if (PUSH_MUSCLES.has(primary)) push++;
      else if (PULL_MUSCLES.has(primary)) pull++;
      else if (LEGS_MUSCLES.has(primary)) legs++;
      else if (CORE_MUSCLES.has(primary)) core++;
    }
    return { push, pull, legs, core, total: push + pull + legs + core };
  }, []) ?? { push: 0, pull: 0, legs: 0, core: 0, total: 0 };

  if (data.total === 0) return null;

  const pushPullRatio = data.pull > 0 ? (data.push / data.pull).toFixed(2) : '—';
  const ratioColor = data.push / (data.pull || 1) > 1.2
    ? 'text-warning'
    : data.push / (data.pull || 1) < 0.8
    ? 'text-info'
    : 'text-success';

  return (
    <section>
      <p className="section-header">Muscle Balance (30 Days)</p>
      <div className="card space-y-4">
        <Bar label="Push (Chest · Delts · Tris)" count={data.push} total={data.total} color="#7c3aed" />
        <Bar label="Pull (Back · Biceps)"         count={data.pull} total={data.total} color="#22c55e" />
        <Bar label="Legs (Quads · Hams · Glutes)" count={data.legs} total={data.total} color="#f59e0b" />
        <Bar label="Core"                          count={data.core} total={data.total} color="#3b82f6" />

        {/* Push:Pull ratio */}
        <div className="border-t border-surface-400 pt-3 flex justify-between items-center">
          <p className="text-xs text-ink-muted">Push : Pull ratio</p>
          <span className={`text-sm font-bold font-mono ${ratioColor}`}>
            {pushPullRatio}
            {data.push / (data.pull || 1) > 1.2 && <span className="text-xs ml-1 text-warning">(more pull recommended)</span>}
          </span>
        </div>
      </div>
    </section>
  );
}
